import { useCallback, useEffect, useRef, useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import ChatbotComposer from "@/components/chatbot-composer";
import ChatbotMessage from "@/components/chatbot-message";
import ChatbotTypingIndicator from "@/components/chatbot-typing-indicator";
import { fetchChatbotChats } from "@/components/chatbot-api";
import useChatbotConversation, { POLL_INTERVAL_MS } from "@/hooks/use-chatbot-conversation";
import useChatbotReadState from "@/hooks/use-chatbot-read-state";
import AuthLayout from "@/layouts/auth-layout";
import { chatbot_chat_path, chatbot_chats_path } from "@/routes";

function formatDate(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Copenhagen"
  }).format(new Date(value));
}

function messageKey(message) {
  return message.id || `${message.role}-${message.created_at}-${message.content}`;
}

function chatTimestamp(chat) {
  return new Date(chat.last_message_at || chat.created_at || 0).getTime();
}

function sortChatsByRecent(chats) {
  return [...chats].sort((firstChat, secondChat) => chatTimestamp(secondChat) - chatTimestamp(firstChat));
}

function replaceUrlWithChat(chat) {
  if (typeof window === "undefined" || !chat?.id) return;

  window.history.replaceState(window.history.state, "", chatbot_chat_path(chat.id));
}

function upsertChat(chats, chat) {
  return sortChatsByRecent([
    chat,
    ...chats.filter((existingChat) => existingChat.id !== chat.id)
  ]);
}

export default function ChatbotChatsIndex({ chats = [], selected_chat: selectedChat }) {
  const [chatList, setChatList] = useState(chats);
  const [question, setQuestion] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(selectedChat?.title || "");
  const messagesEndRef = useRef(null);
  // The chat the conversation below currently belongs to. Compared against the
  // incoming props to tell a real navigation from an ordinary re-render.
  const syncedChatIdRef = useRef(selectedChat?.id || null);
  const chatListVersionRef = useRef(0);

  const onChatChanged = useCallback((chat) => {
    chatListVersionRef.current += 1;
    setChatList((currentChats) => upsertChat(currentChats, chat));
    setTitle(chat.title);
    // Keep the address bar on this chat, but without an Inertia visit: a prop
    // refetch would land with selected_chat still absent and replace the
    // conversation we are showing.
    replaceUrlWithChat(chat);
    syncedChatIdRef.current = chat.id;
  }, []);

  const {
    chat: currentChat,
    messages,
    setMessages,
    processing,
    canStop,
    stopping,
    stop,
    error,
    setError,
    submit,
    openChat
  } = useChatbotConversation({
    initialChat: selectedChat,
    initialMessages: selectedChat?.messages || [],
    onChatChanged
  });

  const hasUnreadResponse = useChatbotReadState({ chatId: currentChat?.id, messages });

  useEffect(() => {
    setChatList(chats);
  }, [chats]);

  // Refresh the overview independently so answers in other chats are visible.
  useEffect(() => {
    const controller = new AbortController();
    let timer;
    let refreshing = false;

    const refreshChats = async () => {
      if (refreshing || controller.signal.aborted) return;
      window.clearTimeout(timer);
      refreshing = true;
      const version = chatListVersionRef.current;
      try {
        if (document.visibilityState === "visible") {
          const nextChats = await fetchChatbotChats({ signal: controller.signal });
          if (!controller.signal.aborted && version === chatListVersionRef.current) setChatList(nextChats);
        }
      } catch (_error) {
        // Keep the last known status and retry after a temporary network error.
      } finally {
        refreshing = false;
        if (!controller.signal.aborted) timer = window.setTimeout(refreshChats, POLL_INTERVAL_MS);
      }
    };

    timer = window.setTimeout(refreshChats, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshChats);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", refreshChats);
    };
  }, [chats]);

  // Only a real navigation may replace the conversation. Re-syncing on any
  // other render would discard a chat this client just created, because
  // selected_chat is still absent from the props at that point.
  useEffect(() => {
    const incomingChatId = selectedChat?.id || null;
    if (incomingChatId === syncedChatIdRef.current) return;

    syncedChatIdRef.current = incomingChatId;
    openChat(selectedChat || null, selectedChat?.messages || []);
    setTitle(selectedChat?.title || "");
    setEditingTitle(false);
  }, [selectedChat, openChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, processing]);

  const sendMessage = async (submission) => {
    if (currentChat?.can_message === false) return false;

    setQuestion("");

    const accepted = await submit(submission);
    if (!accepted) setQuestion(submission.question);

    return accepted;
  };

  const submitTitle = (event) => {
    event.preventDefault();
    if (!currentChat?.can_update || !title.trim()) return;

    router.patch(chatbot_chat_path(currentChat.id), { chat: { title: title.trim() } }, {
      preserveScroll: true,
      onSuccess: () => setEditingTitle(false)
    });
  };

  const deleteChat = () => {
    if (!currentChat?.can_destroy) return;

    router.delete(chatbot_chat_path(currentChat.id));
  };

  return (
    <>
      <Head title="Obsero AI" />

      <section className="chatbot-fullscreen">
        <aside className="chatbot-fullscreen__sidebar">
          <div className="chatbot-fullscreen__sidebar-header">
            <div>
              <div className="workspace-section__eyebrow">Chat</div>
              <h2 className="chatbot-fullscreen__sidebar-title">Chats</h2>
            </div>
            <Link href={chatbot_chats_path({ new: true })} className="btn btn-primary btn-sm">
              Ny chat
            </Link>
          </div>

          <nav className="chatbot-fullscreen__chat-list" aria-label="Chats">
            {chatList.length === 0 ? (
              <div className="chatbot-fullscreen__empty-sidebar">Ingen chats endnu.</div>
            ) : chatList.map((chat) => (
              <Link
                key={chat.id}
                href={chatbot_chat_path(chat.id)}
                className={`chatbot-fullscreen__chat-link ${currentChat?.id === chat.id ? "is-active" : ""}`.trim()}
              >
                <div className="chatbot-fullscreen__chat-heading">
                  <span className="chatbot-fullscreen__chat-name">{chat.title}</span>
                  {chat.processing || (currentChat?.id === chat.id && processing) ? (
                    <span className="spinner-border spinner-border-sm text-secondary flex-shrink-0" role="status" aria-label="Venter på svar" title="Venter på svar" />
                  ) : hasUnreadResponse(chat) ? (
                    <span className="chatbot-fullscreen__unread-dot" role="status" aria-label="Nyt svar" title="Nyt svar" />
                  ) : null}
                </div>
                <small>
                  {chat.owner ? `${chat.owned_by_current_user ? "Dig" : chat.owner.name} · ` : null}
                  {formatDate(chat.last_message_at || chat.created_at)}
                </small>
              </Link>
            ))}
          </nav>
        </aside>

        <main className="chatbot-fullscreen__main">
          <header className="chatbot-fullscreen__header">
            {currentChat ? (
              editingTitle ? (
                <form className="chatbot-fullscreen__title-form" onSubmit={submitTitle}>
                  <input className="form-control" value={title} onChange={(event) => setTitle(event.target.value)} />
                  <button className="btn btn-primary">Gem</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingTitle(false)}>Annuller</button>
                </form>
              ) : (
                <>
                  <div>
                    <div className="workspace-section__eyebrow">Obsero AI</div>
                    <h2 className="chatbot-fullscreen__title">{currentChat.title}</h2>
                  </div>
                  <div className="d-flex gap-2">
                    {currentChat.can_update ? (
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingTitle(true)}>Omdøb</button>
                    ) : null}
                    {currentChat.can_destroy ? (
                      <button type="button" className="btn btn-outline-danger" onClick={deleteChat}>Slet</button>
                    ) : null}
                  </div>
                </>
              )
            ) : (
              <div>
                <div className="workspace-section__eyebrow">Obsero AI</div>
                <h2 className="chatbot-fullscreen__title">Ny chat</h2>
              </div>
            )}
          </header>

          <div className="chatbot-fullscreen__messages">
            {messages.length === 0 ? (
              <div className="chatbot-fullscreen__empty-state">
                <div className="chatbot-fullscreen__empty-icon"><i className="bi bi-robot" aria-hidden="true" /></div>
                <h3>Hvad kan jeg hjælpe med?</h3>
                <p>Stil et spørgsmål om jeres dokumenter, filer, DORA, NIS2 eller compliance-arbejde.</p>
              </div>
            ) : messages.map((message) => (
              <ChatbotMessage
                key={messageKey(message)}
                message={message}
                surface="fullscreen"
                userLabel={currentChat?.owned_by_current_user ? "Dig" : "Bruger"}
              />
            ))}

            {processing ? <ChatbotTypingIndicator surface="fullscreen" /> : null}
            <div ref={messagesEndRef} />
          </div>

          {error ? <div className="alert alert-danger chatbot-fullscreen__error">{error}</div> : null}

          {!currentChat || currentChat.can_message ? (
            <ChatbotComposer
              key={selectedChat?.id || "new"}
              surface="fullscreen"
              question={question}
              setQuestion={setQuestion}
              processing={processing}
              canStop={canStop}
              stopping={stopping}
              onStop={stop}
              onSubmit={sendMessage}
              onError={setError}
              autoFocus
            />
          ) : (
            <div className="alert alert-info chatbot-fullscreen__read-only" role="status">
              Denne chat er skrivebeskyttet. Kun ejeren kan skrive i chatten.
            </div>
          )}
        </main>
      </section>
    </>
  );
}

ChatbotChatsIndex.layout = (page) => (
  <AuthLayout
    header={{
      breadcrumbs: [{ label: "Chat" }],
      title: "Obsero AI"
    }}
  >
    {page}
  </AuthLayout>
);
